import { useRef, useState, useMemo, useCallback } from 'react'
import { useWireState } from '@forminator/react-wire'
import * as store from '@store'
import * as actions from '@actions'
import { useParams, useLocation } from 'react-router-dom'
import ParseAPI from '@api/ParseAPI'
import { usePartnerWorkTimer } from './usePartnerWorkTimer'
import useNavigate from '@hook/useNavigate'
import { useCallbackRef } from '@hook/useCallbackRef'
import {
    RECORD_TYPES,
    RECORD_TYPE_CHECK,
    RECORD_TYPE_DOCUMENT,
    GCS_BUCKET_CHECKS,
    GCS_BUCKET_DOCUMENTS,
    GCS_BUCKET_INDEX_DOCUMENTS,
    CHECK_STATUS_REVIEWED,
    DOCUMENT_STATUS_REVIEWED,
} from '@constants'
import { toast } from 'react-toastify'

// @todo React Router v6 broke this - needs to be updated
// eslint-disable-next-line no-unused-vars
import { useGuardExit } from '@hook/useGuardExit'
import { usePartnersAutoReviewStatus } from './usePartnersAutoReviewStatus'
import { useLabelStudio } from '@hook/useLabelStudio'
import { useFullScreen } from '@hook/useFullScreen'
import Pagination from '@components/shared/Pagination'
import Overlay from '@components/shared/Overlay'
import CircleSpinner from '@components/shared/CircleSpinner'

import LabelStudioEditor from '@components/shared/LabelStudioEditor'
import { FaRegPlayCircle, FaRegPauseCircle, FaRegQuestionCircle } from 'react-icons/fa'
import {
    MdKeyboardArrowLeft,
    MdOutlinePhotoSizeSelectLarge,
    MdOutlinePhotoSizeSelectActual,
    MdFullscreen,
    MdFullscreenExit,
} from 'react-icons/md'

import './Partners.css'

const recordTypesMap = {
    [RECORD_TYPE_CHECK]: {
        endpoint: 'checks',
        statusKey: 'status',
        statusValue: CHECK_STATUS_REVIEWED,
    },
    [RECORD_TYPE_DOCUMENT]: {
        endpoint: 'document',
        statusKey: 'reviewStatus',
        statusValue: DOCUMENT_STATUS_REVIEWED,
    },
}

const PartnersAnnotate = () => {
    
    const params = useParams()
    const location = useLocation()
    const navigate = useNavigate()
    
    const refContainer = useRef()
    const [refEditor, editorNode] = useCallbackRef()
    
    const [record, setRecord] = useState(null)
    const [pageIndex, setPageIndex] = useState(0)
    const [workTimerPaused, setWorkTimerPaused] = useState(false)
    
    // @todo React Router v6 broke this - needs to be updated
    // eslint-disable-next-line no-unused-vars
    const [workSaved, setWorkSaved] = useState(false)
    
    // @todo React Router v6 broke this - needs to be updated
    // eslint-disable-next-line no-unused-vars
    const [workSaving, setWorkSaving] = useState(false)
    
    const [showExpandedAnnotationView, setShowExpandedAnnotationView] = useWireState(store.showExpandedAnnotationView)
    
    const recordId = useMemo(() => params?.recordId, [params])
    
    // Record type (check, document, etc.) determined
    // from the URL path, since we can't pass it through the route
    const recordType = useMemo(() => {
        
        const value = RECORD_TYPES.find(it => location.pathname.includes(`/${it}/`))
        
        if (!value) {
            toast.error('Invalid record type')
            setTimeout(() => navigate('/partners'), 3000)
            return null
        }
        
        return value
        
    }, [location])
    
    const gcsBucket = useMemo(() => {
        switch (recordType) {
            case RECORD_TYPE_CHECK: return GCS_BUCKET_CHECKS
            case RECORD_TYPE_DOCUMENT: return record?.isParse ? GCS_BUCKET_INDEX_DOCUMENTS : GCS_BUCKET_DOCUMENTS
            default: return null
        }
    }, [recordType])
    
    const editorIsReady = useMemo(() => (
        editorNode &&
        record !== null
    ), [editorNode, record])
    
    const { labelStudioLoading, labelStudioError } = useLabelStudio({
        enabled: editorIsReady,
        url: null,
        gcsBucket,
        editorNode,
        record,
        recordType,
        pageIndex,
        setRecord,
        showAllLabels: true,
        showPrivateLabels: true,
        useOrganizationLabels: recordType === RECORD_TYPE_CHECK,
        useCollectionLabels: recordType === RECORD_TYPE_DOCUMENT,
    })
    
    const { isFullScreen, toggleFullScreen } = useFullScreen(refContainer)
    
    usePartnersAutoReviewStatus(recordType, recordId, setRecord)
    
    const workTimeElapsed = usePartnerWorkTimer(labelStudioLoading || workTimerPaused)
    
    const saveWork = useCallback(async (returnToDocuments = true) => {
        
        const { endpoint, statusKey, statusValue } = recordTypesMap[recordType]
        
        try {
            
            await ParseAPI.put(`/${endpoint}/review/${recordId}`, {
                [statusKey]: statusValue,
                annotationWorkDurationSec: workTimeElapsed.seconds,
            })
            
            const updatedRecord = await actions.fetchDocumentById(recordId)
            
            setRecord(updatedRecord)
            
            if (returnToDocuments)
                navigate.toPartners()
            
        } catch (e) {
            
            // @todo show error
            console.error('saveWork', e)
            
        }
        
    }, [recordType, recordId, workTimeElapsed])
    
    // @todo React Router v6 broke this - needs to be updated
    /* const onGuardedExit = useCallback(async () => {
        
        setWorkSaving(true)
        
        await saveWork()
        
        setWorkSaving(false)
        setWorkSaved(true)
        
    }, [workSaved])
    
    useGuardExit(!workSaved, onGuardedExit) */
    
    if (!record || !recordType) return (
        <div className="PartnersAnnotate">
            <CircleSpinner className="absolute-centered" />
        </div>
    )
    
    return (
        
        <div ref={refContainer} className="PartnersAnnotate">
            
            <header className="flex justify-between items-center content-center">
                
                <div className="flex items-center content-center">
                    
                    <button
                        className="mr-2 btn btn-link flex items-center content-center"
                        onClick={navigate.toPartners}>
                        <MdKeyboardArrowLeft />
                        <span className="ml-2">Back</span>
                    </button>
                    
                    <h6 className="m-0 text-left text-base">
                        Editing {record?.name}
                    </h6>
                
                </div>
                
                <div className="work-timer flex justify-center items-center content-center">
                    <div className="timer">
                        {workTimeElapsed?.paddedHours}:{workTimeElapsed?.paddedMinutes}:{workTimeElapsed?.paddedSeconds}
                    </div>
                    <button
                        className="ml-3 btn btn-link btn-icon"
                        onClick={() => setWorkTimerPaused(!workTimerPaused)}>
                        {workTimerPaused ? <FaRegPlayCircle /> : <FaRegPauseCircle />}
                    </button>
                    <button
                        className="btn btn-link btn-icon"
                        onClick={() => alert('@todo help popup')}>
                        <FaRegQuestionCircle />
                    </button>
                    <button
                        className="ml-4 btn btn-success btn-sm"
                        onClick={saveWork}>
                        COMPLETE WORK
                    </button>
                </div>
                
                <div className="flex justify-end items-center content-center">
                    <Pagination
                        count={record?.pages?.length ?? 0}
                        itemsPerPage={1}
                        pageRangeDisplayed={3}
                        forcePage={pageIndex}
                        setPageIndex={setPageIndex} />
                    <button
                        className="btn btn-link"
                        title="Show expanded document view"
                        onClick={() => setShowExpandedAnnotationView(!showExpandedAnnotationView)}>
                        {showExpandedAnnotationView
                            ? <MdOutlinePhotoSizeSelectActual />
                            : <MdOutlinePhotoSizeSelectLarge />
                        }
                    </button>
                    <button
                        className="btn btn-link"
                        onClick={() => toggleFullScreen(refContainer)}>
                        {isFullScreen ? <MdFullscreenExit /> : <MdFullscreen />}
                    </button>
                </div>
            
            </header>
            
            {labelStudioLoading && (
                <CircleSpinner className="absolute-centered" />
            )}
            
            <LabelStudioEditor
                ref={refEditor}
                error={labelStudioError} />
            
            <Overlay className="bg-secondary" open={workSaving} opacity="0.6">
                <div className="w-full text-center absolute-centered">
                    <CircleSpinner size="30px" indicatorColor="#777777" />
                    <div className="mt-5 ml-4 text-center">
                        SAVING WORK
                    </div>
                </div>
            </Overlay>
        
        </div>
        
    )
    
}

export default PartnersAnnotate
